import React from "react";
import { chain, compact, filter, keys, map, mapObject, reduce, values } from "underscore";
import {
    ALL_OPTIONS,
    CUBE_PERCENT_MEASURES,
    DimenionType,
    REVSTACK_CHART_COLORS,
    REVSTACK_MEASURES,
    WIDGET_QUERY_CONFIG,
    WidgetQueryType,
} from "../constants";
import {
    CommonWidgetMetrics,
    CubeQueryType,
    CubeTemplate,
    DateConfig,
    ICompetitionDataSourceItem,
    IRevstackFilterOption,
    OptionValues,
    RevstackCubeIds,
    RevstackFilterKey,
    RevstackFrequencySlicerOptions,
    SeriesGranularity,
    SubModule,
    TableExportFormats,
    TSetHideCompetition,
} from "../common-types";
import { flattenWidgetStructure } from "./renderers";
import {
    capitalizeWords,
    generateRandomString,
    getActiveModule,
    titleCase,
    toSafeKey,
} from "@ts/utils";
import store from "@/store";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import moment from "moment";
import Highcharts from "highcharts";
import { updateTrendsState } from "@/features/modalSlice";
import { showToast } from "@/ts/modals";
import { getAdvancedFilters } from "@/ts/retrieve-data";
import { updateAdditionalFiltersData } from "@/features/revstackWidgetsSlice";
import * as Sentry from "@sentry/react";
import { BrandTypes } from "@/components/widgets/filter-widgets/TopLevelFilters";


export const getPriceTypeLabel = (countryCode: string): string => {
    return countryCode === CommonWidgetMetrics.IN ? CommonWidgetMetrics.MRP.toUpperCase() : CommonWidgetMetrics.LP.toUpperCase();
};

export const safeFormatNumber = (
    value: any,
    decimals = 1,
    fallback = 0
): number => {
    if (value === null || value === undefined || isNaN(value)) {
        return fallback;
    }
    return +Number(value).toFixed(decimals);
};

export const normalizeRevstackLayoutFilters = (filters: any) => {
    const normalizedFilters = { ...filters };

    if (normalizedFilters.metricType?.selected === CommonWidgetMetrics.BY_VOLUME) {
        normalizedFilters.priceType = {
            ...normalizedFilters.priceType,
            selected: null,
        };
    } else if (normalizedFilters.metricType?.selected === CommonWidgetMetrics.BY_VALUE) {
        // Only set to "mrp" if priceType.selected is null or undefined
        if (!normalizedFilters.priceType?.selected) {
            normalizedFilters.priceType = {
                ...normalizedFilters.priceType,
                selected: CommonWidgetMetrics.MRP,
            };
        }
    }

    return normalizedFilters;
};

// Function to check if a color is light or dark
export const isLightColor = (color: string): boolean => {
    const hex = color.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
};

export const getHeatmapColorStyle = (
    value: number,
    min: number,
    max: number
) => {
    const baseRed = "223, 42, 22"; // #DF2A16
    const baseGreen = "25, 135, 84"; // #198754

    let opacity;
    if (value < 0) {
        opacity = Math.abs(value) / Math.abs(min);
        opacity = Math.min(opacity, 1);
        return `rgba(${baseRed}, ${opacity.toFixed(2)})`;
    } else if (value > 0) {
        opacity = value / max;
        opacity = Math.min(opacity, 1);
        return `rgba(${baseGreen}, ${opacity.toFixed(2)})`;
    } else {
        return `#adb5bd`;
    }
};

//TODO: Fill rate has to be remove from this @nijo...

export const measuresToSkip = [
    CommonWidgetMetrics.BRANDED_SEARCH,
    CommonWidgetMetrics.FILL_RATE,
];

export const getRevstackColors = (
    colorsObject: typeof REVSTACK_CHART_COLORS
) => {
    // Get an object with arrays of sorted colors
    const sortedColorArrays = mapObject(colorsObject, (shades) => {
        return chain(shades)
            .pairs()
            .sortBy(([key]) => -parseInt(key))
            .pluck(1)
            .value();
    });
    // Flatten all arrays into one
    return Object.values(sortedColorArrays).flat();
};

const generateRelativePeriodConfig = (
    relativePeriodConfig: { granularity?: RevstackFrequencySlicerOptions; value?: number, isDynamicPeriodValue?: boolean },
    currentRange: string[],
) => {
    let periodGranularity =
        relativePeriodConfig?.granularity ?? SeriesGranularity.MONTH;
    const periodValue = relativePeriodConfig?.value ?? 6;

    const currentStartDate = dayjs(currentRange[0]);
    const currentEndDate = dayjs(currentRange[1]);

    // Check if current end date is today AND the first day of the month
    const isTodayFirstOfMonth =
        currentEndDate.isSame(dayjs(), SeriesGranularity.DAY) &&
        currentEndDate.isSame(
            currentEndDate.startOf(SeriesGranularity.MONTH),
            SeriesGranularity.DAY
        );

    if (relativePeriodConfig?.isDynamicPeriodValue) {
        periodGranularity = RevstackFrequencySlicerOptions.Daily;
    }
    const modifiedEndDate = isTodayFirstOfMonth
        ? currentEndDate
            .subtract(1, SeriesGranularity.DAY)
            .endOf(SeriesGranularity.MONTH)
            .format("YYYY-MM-DD")
        : currentEndDate.format("YYYY-MM-DD");

    // Calculate start date to maintain 6-month range
    const modifiedStartDate = relativePeriodConfig?.isDynamicPeriodValue ? currentStartDate.format("YYYY-MM-DD") : dayjs(modifiedEndDate)
        .subtract(periodValue - 1, periodGranularity)
        .startOf(periodGranularity)
        .format("YYYY-MM-DD");

    return {
        modifiedStartDate,
        modifiedEndDate,
        periodGranularity,
    };
};

export const cubeQueryTemplateGenerator = (
    dashBoardCubeId = "performance_overview",
    queryType: string,
    dateConfig: DateConfig,
    filters: any,
    relativePeriodConfig?: { granularity: RevstackFrequencySlicerOptions; value: number },
    queryMetaInfo?: any
): CubeTemplate => {
    const { currentRange, previousRange, granularity } = dateConfig;
    const timeDimension = `${dashBoardCubeId}.crawl_date`;

    // Extract and store filter values if filterToSkip is specified
    const cleanedFilters = (() => {
        if (!queryMetaInfo?.filterToSkip && !queryMetaInfo?.filtersToIgnore) {
            return filters;
        }

        if (queryMetaInfo?.filterToSkip) {
            const matchingFilter = filters.find((fil: any) => {
                const [, field] = fil.member?.split(".") ?? [];
                return field === queryMetaInfo.filterToSkip;
            });

            if (!matchingFilter) {
                return filters;
            }

            // Store the values that will be skipped
            queryMetaInfo.skippedFilterValues = matchingFilter.values;
        }

        // Remove the matching filters from the query
        const filtersToSkip = compact([
            queryMetaInfo?.filterToSkip,
            ...(queryMetaInfo.filtersToIgnore ?? []),
        ]);

        // Convert to a Set for O(1) exact matching
        const skipSet = new Set(filtersToSkip);

        return filters.filter((fil: any) => {
            // fil.member is always cubeId.field
            const [, field] = fil.member?.split(".") ?? [];

            // Proper exact match (NO includes)
            return !skipSet.has(field);
        });
    })();

    const cubeTemplate: CubeTemplate = {
        payload: {
            timeDimensions: [],
            filters: cleanedFilters,
            measures: [],
            dimensions: [],
        },
    };

    cubeTemplate.queryType = queryType;
    cubeTemplate.sectionId = dashBoardCubeId;
    switch (queryType) {
        case CubeQueryType.SERIES:
            cubeTemplate.payload.timeDimensions.push({
                dimension: timeDimension,
                dateRange: currentRange,
                granularity,
            });
            break;
        case CubeQueryType.VALUE_DELTA:
        case CubeQueryType.DIMENSION_VALUE_DELTA:
        case CubeQueryType.MULTI_MEASURE_VALUE_DELTA:
        case CubeQueryType.DIMENSION_PIVOT_CONSOLIDATION:
        case CubeQueryType.DIMENSION_PIE_CHART: {
            cubeTemplate.payload.timeDimensions.push({
                dimension: timeDimension,
                compareDateRange: [currentRange, previousRange],
            });
            break;
        }

        case CubeQueryType.DIMENSION_BAR_CHART: {
            if (relativePeriodConfig && Object.keys(relativePeriodConfig).length > 0) {
                const { modifiedStartDate, modifiedEndDate, periodGranularity } =
                    generateRelativePeriodConfig(
                        relativePeriodConfig,
                        currentRange
                    );

                cubeTemplate.payload.timeDimensions.push({
                    dimension: timeDimension,
                    dateRange: [modifiedStartDate, modifiedEndDate],
                    granularity: periodGranularity,
                });
            } else {
                cubeTemplate.payload.timeDimensions.push({
                    dimension: timeDimension,
                    compareDateRange: [currentRange, previousRange],
                });
            }
            break;
        }
        case CubeQueryType.DIMENSION_TIME_SERIES: {
            const { modifiedStartDate, modifiedEndDate, periodGranularity } =
                generateRelativePeriodConfig(
                    relativePeriodConfig,
                    currentRange
                );

            cubeTemplate.payload.timeDimensions.push({
                dimension: timeDimension,
                dateRange: [modifiedStartDate, modifiedEndDate],
                granularity: periodGranularity,
            });
            break;
        }
        default:
            console.error("Invalid query type");
            return {};
    }

    return cubeTemplate;
};

/**
 * Review this new query building by cube id logic
 */
export const buildQueriesFromWidgetsByCubeId = (
    widgets: any,
    dateConfig?: any,
    customFilters?: any[]
) => {
    const flatWidgets = flattenWidgetStructure(widgets);
    const uniqueCubeIds = [
        ...new Set(
            flatWidgets.map(
                (widget: any) => widget.cubeId || RevstackCubeIds.PO_REGIONAL
            )
        ),
    ];

    const allQueries: any[] = [];

    uniqueCubeIds.forEach((cubeId: string) => {
        // Generate cube-specific filters
        const { cubeDateObject, cubeFilters } = generateCubeFilters(cubeId);

        // Use provided dateConfig or the generated one
        const finalDateConfig = dateConfig || cubeDateObject;
        const finalFilters = customFilters || cubeFilters;

        // Filter widgets for this specific cubeId
        const cubeSpecificWidgets = flatWidgets.filter(
            (widget: any) => (widget.cubeId || RevstackCubeIds.PO_REGIONAL) === cubeId
        );

        if (cubeSpecificWidgets.length > 0) {
            const cubeQueries = buildCubeQueriesFromSectionConfig(
                cubeSpecificWidgets,
                finalDateConfig,
                finalFilters
            );
            allQueries.push(...cubeQueries);
        }
    });

    return allQueries;
};

export const buildCubeQueriesFromSectionConfig = (
    widgetRows: any,
    dateConfig: any,
    filters: any[]
) => {
    const queryGroupsByCubeId = groupWidgetsByQuery(widgetRows);
    const allQueries: any[] = [];

    // Generate queries for each cubeId
    Object.entries(queryGroupsByCubeId).forEach(([cubeId, queryGroups]) => {
        const queries = generateQueriesFromGroups(
            queryGroups,
            cubeId,
            dateConfig,
            filters
        );
        allQueries.push(...queries);
    });

    return allQueries;
};

//!ToDo: Improve this function to handle nested widgets

// Helper function to group widgets by their query characteristics
const groupWidgetsByQuery = (widgetRows: any) => {
    const queryGroupsByCubeId: Record<
        string,
        Record<
            string,
            {
                measures: Set<string>;
                dimensions: Set<string>;
                universeDimensions: Set<string>;
                relativePeriodConfig?: any;
                queryMetaInfo?: any;
                excludeFilter: string[];
                isExcludeDimension?: boolean;
                groupedWidgetIds: string[];
            }
        >
    > = {};

    // Use the new flattening utility to handle any level of nesting
    const flatWidgets = flattenWidgetStructure(widgetRows);

    flatWidgets.forEach((widget: any) => {
        const measures = widget.measures || [];
        const queryDimensions = widget.queryDimensions || [];
        const cubeId = widget.cubeId || RevstackCubeIds.PO_REGIONAL; // Default to PO_REGIONAL if not specified
        const widgetQueries =
            WIDGET_QUERY_CONFIG[
            widget.type as keyof typeof WIDGET_QUERY_CONFIG
            ];

        if (!widgetQueries?.length) {
            showToast(
                "danger",
                `Widget ${widget.identifier} has no queries defined.`
            );
            return;
        }

        // Initialize cubeId group if it doesn't exist
        if (!queryGroupsByCubeId[cubeId]) {
            queryGroupsByCubeId[cubeId] = {};
        }

        widgetQueries.forEach((query: any) => {
            processQueryGroup(
                queryGroupsByCubeId[cubeId],
                query,
                widget,
                queryDimensions,
                measures,
                cubeId,
                widget.identifier
            );
        });

        if (widget?.queryMetaInfo?.consolidatedQueryConfig) {
            processConsolidatedQueryConfig({
                queryGroupsByCubeId,
                widgetQueries,
                widget,
                baseQueryMetaInfo: widget.queryMetaInfo,
                consolidatedConfig:
                    widget.queryMetaInfo.consolidatedQueryConfig,
                fallbackQueryDimensions: queryDimensions,
                fallbackMeasures: measures,
                cubeId,
                identifierSuffix: WidgetQueryType.CONSOLIDATED,
            });
        }

        if (widget?.expandRowQueryConfig) {
            const expandedConfig = widget.expandRowQueryConfig;

            const expandedQueryDimensions =
                expandedConfig.queryDimensions || queryDimensions;

            widgetQueries.forEach((query: any) => {
                const expandedQueryMetaInfo =
                    expandedConfig.queryMetaInfo || widget.queryMetaInfo;
                const expandedIsExcludeDimension =
                    expandedConfig.isExcludeDimension;
                const expandedUniverseDimensions =
                    expandedConfig.universeDimensions || widget.universeDimensions;
                const expandedWidget = {
                    ...widget,
                    queryMetaInfo: expandedQueryMetaInfo,
                    ...(expandedIsExcludeDimension && {
                        isExcludeDimension: expandedIsExcludeDimension,
                    }),
                    ...(expandedUniverseDimensions && {
                        universeDimensions: expandedUniverseDimensions,
                    }),
                };

                processQueryGroup(
                    queryGroupsByCubeId[cubeId],
                    query,
                    expandedWidget,
                    expandedQueryDimensions,
                    measures,
                    cubeId,
                    `${widget.identifier}_${WidgetQueryType.EXPANDED}`
                );

                // If expanded config's query meta info consists consolidatedQueryConfig, repeat the same logic
                if (expandedQueryMetaInfo?.consolidatedQueryConfig) {
                    processConsolidatedQueryConfig({
                        queryGroupsByCubeId,
                        widgetQueries,
                        widget,
                        baseQueryMetaInfo: expandedQueryMetaInfo,
                        consolidatedConfig:
                            expandedQueryMetaInfo.consolidatedQueryConfig,
                        fallbackQueryDimensions: expandedQueryDimensions,
                        fallbackMeasures: measures,
                        cubeId,
                        identifierSuffix: WidgetQueryType.EXP_CONSOLIDATED,
                    });
                }
            });
        }
    });

    return queryGroupsByCubeId;
};

// Helper function to process consolidated query configuration
const processConsolidatedQueryConfig = ({
    queryGroupsByCubeId,
    widgetQueries,
    widget,
    baseQueryMetaInfo,
    consolidatedConfig,
    fallbackQueryDimensions,
    fallbackMeasures,
    cubeId,
    identifierSuffix,
}: {
    queryGroupsByCubeId: any;
    widgetQueries: any[];
    widget: any;
    baseQueryMetaInfo: any;
    consolidatedConfig: any;
    fallbackQueryDimensions: string[];
    fallbackMeasures: any[];
    cubeId: string;
    identifierSuffix: string;
}) => {
    const consolidatedQueryDimensions =
        consolidatedConfig.queryDimensions || fallbackQueryDimensions;

    const consolidatedMeasures =
        consolidatedConfig.measures || fallbackMeasures;

    widgetQueries.forEach((query: any) => {
        const consolidatedQueryMetaInfo = {
            ...baseQueryMetaInfo,
            resultKey:
                consolidatedConfig.resultKey ||
                baseQueryMetaInfo.resultKey,
            // Remove the consolidatedQueryConfig to avoid recursion
            consolidatedQueryConfig: undefined,
        };

        const consolidatedWidget = {
            ...widget,
            queryMetaInfo: consolidatedQueryMetaInfo,
            isExcludeDimension: consolidatedConfig.isExcludeDimension,
            universeDimensions: [],
        };

        processQueryGroup(
            queryGroupsByCubeId[cubeId],
            query,
            consolidatedWidget,
            consolidatedQueryDimensions,
            consolidatedMeasures,
            cubeId,
            `${widget.identifier}_${identifierSuffix}`
        );
    });
};

// Helper function to create a unique group key for query grouping
const createGroupKey = (query: any, widget: any, queryDimensions: string[]) => {
    const dimensionsForQuery = query.isDimensioned ? queryDimensions : [];
    const periodConfigKey = JSON.stringify(widget.relativePeriodConfig || {});
    const metaInfoKey = widget.queryMetaInfo
        ? JSON.stringify(widget.queryMetaInfo)
        : "";
    const uniqueKey = widget.queryMetaInfo ? `_${widget.identifier}` : "";
    const excludeFilterKey = JSON.stringify(widget.excludeFilter || []);
    const isExcludeDimensionKey = widget.isExcludeDimension ? "true" : "";
    const universeDimensionsKey = JSON.stringify(
        widget.universeDimensions || []
    );

    return `${query.type}_${JSON.stringify(
        dimensionsForQuery
    )}_${periodConfigKey}_${metaInfoKey}${uniqueKey}_${excludeFilterKey}_${isExcludeDimensionKey}_${universeDimensionsKey}`;
};

// Helper function to add measures to a query group
const addMeasuresToGroup = (group: any, measures: CommonWidgetMetrics[]) => {
    measures.forEach((measure: CommonWidgetMetrics) => {
        if (!measuresToSkip.includes(measure)) {
            group.measures.add(measure);
        }
    });
};

// Helper function to add dimensions to a query group
const addDimensionsToGroup = (
    group: any,
    dimensionKey: DimenionType,
    dimensions: string[],
    cubeId: string
) => {
    dimensions.forEach((dimension: string) => {
        group[dimensionKey].add(`${cubeId}.${dimension}`);
    });
};

// Helper function to process a query group (used for both regular and consolidated queries)
const processQueryGroup = (
    queryGroups: Record<string, any>,
    query: any,
    widget: any,
    queryDimensions: string[],
    measures: CommonWidgetMetrics[],
    cubeId: string,
    widgetIdentifier: string
) => {
    const groupKey = createGroupKey(query, widget, queryDimensions);

    if (!queryGroups[groupKey]) {
        queryGroups[groupKey] = {
            measures: new Set(),
            dimensions: new Set(),
            universeDimensions: new Set(),
            relativePeriodConfig: widget.relativePeriodConfig,
            queryMetaInfo: widget.queryMetaInfo,
            excludeFilter: widget.excludeFilter || [],
            isExcludeDimension: widget.isExcludeDimension || false,
            groupedWidgetIds: [],
        };
    }

    addMeasuresToGroup(queryGroups[groupKey], measures);
    addDimensionsToGroup(
        queryGroups[groupKey],
        DimenionType.DIMENSION,
        query.isDimensioned ? queryDimensions : ([] as string[]),
        cubeId
    );

    if (widget.universeDimensions?.length) {
        addDimensionsToGroup(
            queryGroups[groupKey],
            DimenionType.UNIVERSE_DIMENSION,
            widget.universeDimensions,
            cubeId
        );
    }

    queryGroups[groupKey].groupedWidgetIds.push(widgetIdentifier);
};

// Helper function to segregate measures based on CUBE_PERCENT_MEASURES
export const segregateMeasures = (
    measures: CommonWidgetMetrics[],
    cubeId: string
) => {
    const percMeasures: string[] = [];
    const nonPercMeasures: string[] = [];

    measures.forEach((measure: CommonWidgetMetrics) => {
        const isPercMeasure = CUBE_PERCENT_MEASURES.includes(measure);

        if (isPercMeasure) {
            percMeasures.push(`${cubeId}.${getPreparedMetric(measure)}`);
        } else {
            nonPercMeasures.push(`${cubeId}.${getPreparedMetric(measure)}`);
        }
    });

    return { percMeasures, nonPercMeasures };
};

// Helper function to create a query template
export const createQueryTemplate = (
    group: any,
    measures: string[],
    cubeId: string,
    queryType: string,
    dateConfig: any,
    filters: any[],
    isPercQuery: boolean,
    excludeFilter: string[] = [],
    isExcludeDimension = false,
    universeDimensions: string[] = []
) => {
    const template: any = cubeQueryTemplateGenerator(
        cubeId,
        queryType,
        dateConfig,
        filters,
        group.relativePeriodConfig ?? null,
        group.queryMetaInfo ?? null
    );

    if (template.payload) {
        template.payload.measures = measures;
        template.payload.dimensions = Array.from(group.dimensions);
    }

    if (group.queryMetaInfo) {
        template.queryMetaInfo = group.queryMetaInfo;
    }

    template.groupedWidgetIds = group.groupedWidgetIds;
    template.isPercQuery = isPercQuery;
    excludeFilter.length && (template.excludeFilter = excludeFilter);
    universeDimensions.length && (template.universeDimensions = universeDimensions);
    isExcludeDimension && (template.isExcludeDimension = isExcludeDimension);

    return template;
};

// Helper function to generate queries from grouped data
const generateQueriesFromGroups = (
    queryGroups: Record<string, any>,
    cubeId: string,
    dateConfig: any,
    filters: any[]
) => {
    const allQueries: any[] = [];

    Object.entries(queryGroups).forEach(([groupKey, group]) => {
        const [queryType] = groupKey.split("_");
        const allMeasures = Array.from(group.measures);
        const { percMeasures, nonPercMeasures } = segregateMeasures(
            allMeasures as CommonWidgetMetrics[],
            cubeId
        );

        // Create query for non-percentage measures
        if (nonPercMeasures.length > 0) {
            const nonPercTemplate = createQueryTemplate(
                group,
                nonPercMeasures,
                cubeId,
                queryType,
                dateConfig,
                filters,
                false
            );
            allQueries.push(nonPercTemplate);
        }

        // Create query for percentage measures
        if (percMeasures.length > 0) {
            const percTemplate = createQueryTemplate(
                group,
                percMeasures,
                cubeId,
                queryType,
                dateConfig,
                filters,
                true,
                group.excludeFilter || [],
                group.isExcludeDimension || false,
                Array.from(group.universeDimensions) || []
            );
            allQueries.push(percTemplate);
        }
    });

    return allQueries;
};

const processRevstackFilterValues = (
    defaultValues: (string | number)[],
    filterKey: string | null,
    filterValue: (string | number) | (string | number)[] | null
): (string | number)[] => {
    // If filterKey doesn't exist, filterValue is null, or filterValue is set to all,
    // return default values
    if (
        !filterKey ||
        filterValue === null ||
        filterValue === ALL_OPTIONS.value
    ) {
        return defaultValues;
    }

    // If filterValue is an array, return it directly
    if (Array.isArray(filterValue)) {
        // If array contains "all", return default values
        if (filterValue.includes(ALL_OPTIONS.value)) {
            return defaultValues;
        }
        return filterValue;
    }

    // If filterValue is not an array, enclose it in an array
    return [filterValue];
};

//TODO: Refactor/Optimise this fn @nijo
export const generateCubeFilters = (
    cubeId: string,
    filterKey: string = null,
    filterValue: (string | number) | (string | number)[] = null,
    isfiltersRequest = false
) => {
    const clientSubGroupId = store.getState().clientSubGroupId.value;
    const selectedDateRange = store.getState().selectedDateRange.value;
    const revstackChannel = store.getState().revstackChannel.value;
    const { compareDateRange, dateRange } = selectedDateRange;
    const mainFilters = store.getState().mainFilters.value;
    const drilldownFilters = store.getState().drilldownFilters.value as any;
    const trendsFilters = store.getState().trendsFilters.value as any;
    const relativeFilters = store.getState().relativeFilters.value as any;
    const skuMainFilters = store.getState().skuMainFilters.value as any;
    //TODO: This has to be used in the payload @Nijo
    const additionalFilters = store.getState().additionalFilters.value as any;
    const showRevstackDrilldown =
        store.getState().showRevstackPopup.value.drilldown.show;

    const showRevstackTrends =
        store.getState().showRevstackPopup.value.trends.show;

    const showRevstackChangeSku = store.getState().showRevstackPopup.value.changeSku.show;

    const drilldownCategory = drilldownFilters?.selected?.category;
    const drilldownSubcategory = drilldownFilters?.selected?.subcategory;
    const skuCategory = skuMainFilters.selected.category;
    const skuSubcategory = skuMainFilters.selected.subcategory;
    const skuBrand = skuMainFilters.selected.brand;
    const paCrossChannelSkuId = skuMainFilters.selected.cross_channel_sku_id;

    const trendsCategory = trendsFilters?.selected?.category;
    //TODO: Can we fig out a better implementation for this ?
    const drilldownSecondaryFilterRegion = drilldownFilters?.selected?.[`secondary_${RevstackFilterKey.REGION}`];
    const trendsSecondaryFilterRegion = trendsFilters?.selected?.[`secondary_${RevstackFilterKey.REGION}`];
    const trendsSecondaryFilterChannel = trendsFilters?.selected?.channel
    const revstackChannels = store.getState().revstackChannelsList.value;

    const allChannelIds = revstackChannels?.map((channel: any) => channel.id);

    const drilldownCrossChannelSkuId = drilldownFilters?.selected?.[RevstackFilterKey.CROSS_CHANNEL_SKU_ID];

    const sku = relativeFilters.skus;

    const isValidCategoryFilter = (category: any) =>
        category && category[0] !== ALL_OPTIONS.value;

    const activeModule = getActiveModule().activeModule;

    // Only use SKU-related filters if we have SKU from relativeFilters and active module is Product Analysis
    const hasRelativeSkuFilter = relativeFilters?.skus && relativeFilters.skus.length > 0;
    const isProductAnalysisModule = activeModule === SubModule.PRODUCT_ANALYSIS;

    const shouldUseTrendsCategory = showRevstackTrends && isValidCategoryFilter(trendsCategory);
    const shouldUseDrilldownCategory = showRevstackDrilldown && isValidCategoryFilter(drilldownCategory);
    const shouldUseSkuCategory = hasRelativeSkuFilter && isProductAnalysisModule && !showRevstackTrends
    const shouldUseSkuSubcategory = hasRelativeSkuFilter && isProductAnalysisModule && !showRevstackTrends
    const shouldUseSkuBrand = hasRelativeSkuFilter && isProductAnalysisModule && !showRevstackTrends
    const shouldUseSku = hasRelativeSkuFilter && isProductAnalysisModule && !showRevstackChangeSku && !showRevstackTrends
    const shouldUsePACrossChannelSkuId = hasRelativeSkuFilter && isProductAnalysisModule && !showRevstackChangeSku && !showRevstackTrends

    // Remove after product analytics data integration is done
    // const shouldUseSkuCategory = false;
    // const shouldUseSkuSubcategory = false;
    // const shouldUseSkuBrand = false;
    // const shouldUseSku = false;


    // Determine category source based on context
    // If else is written for better readability
    let categorySource;
    if (shouldUseTrendsCategory) {
        categorySource = trendsCategory;
    } else if (shouldUseDrilldownCategory) {
        categorySource = drilldownCategory;
    } else if (shouldUseSkuCategory && showRevstackChangeSku) {
        categorySource = mainFilters.categories;
    } else if (shouldUseSkuCategory) {
        categorySource = skuCategory;
    } else {
        categorySource = mainFilters.categories;
    }

    const categoryIds = filter(
        categorySource,
        (category: number) => category > 0
    );

    // Determine subcategory source based on context
    // If else is written for better readability
    let subcategorySource;
    if (showRevstackDrilldown && drilldownSubcategory && drilldownSubcategory[0] !== ALL_OPTIONS.value) {
        subcategorySource = drilldownSubcategory;
    } else if (shouldUseSkuSubcategory && showRevstackChangeSku) {
        subcategorySource = mainFilters.subcategories;
    } else if (shouldUseSkuSubcategory) {
        subcategorySource = skuSubcategory;
    } else {
        subcategorySource = mainFilters.subcategories;
    }

    const subcategoryIds = filter(
        subcategorySource,
        (subcategory: number) => subcategory > 0
    );

    // Determine brand source based on context
    // If else is written for better readability
    let brandSource;
    if (shouldUseSkuBrand && showRevstackChangeSku) {
        brandSource = mainFilters.brands;
    } else if (shouldUseSkuBrand) {
        brandSource = skuBrand;
    } else {
        brandSource = mainFilters.brands;
    }

    const brandIds = filter(
        brandSource,
        (brand: number) => brand > 0
    );


    const cubeDateObject = {
        currentRange: [dateRange.start, dateRange.end],
        previousRange: [compareDateRange.start, compareDateRange.end],
        granularity: "day",
    };

    const brandFilterKey = cubeId.includes("keyword")
        ? RevstackFilterKey.BRAND_FILTER
        : RevstackFilterKey.BRAND;

    const skuFilterKey = cubeId.includes("keyword")
        ? RevstackFilterKey.SKU_FILTER
        : RevstackFilterKey.SKU;

    const advMetaData = store.getState().additionalFilters.value.tagTypes;
    const appliedFilters = additionalFilters?.state?.appliedFilters || {};
    const advancedFilters = Object.entries(appliedFilters)
        .filter(([_, tagIds]) => Array.isArray(tagIds) && tagIds.length > 0)
        .map(([tag_type_id, tagIds]) => {
            const filterMetadata = advMetaData?.find((f: { label: string; value: number }) => 
                f.value === Number(tag_type_id)
            );
            return {
                values: tagIds as number[],
                member: `${cubeId}.${toSafeKey(filterMetadata?.label || '')}_id`,
                operator: "equals",
            };
        });

    //TODO: @sarvesh @vaheed we have to refactor these logics...
    const secondaryFilterOptionExists = drilldownSecondaryFilterRegion && keys(drilldownSecondaryFilterRegion)?.length > 0 || trendsSecondaryFilterRegion && keys(trendsSecondaryFilterRegion)?.length > 0
    const shouldShowSecondaryFilter = (showRevstackDrilldown || showRevstackTrends) && secondaryFilterOptionExists;
    const activeSecondaryFilterRegion = showRevstackTrends ? trendsSecondaryFilterRegion : drilldownSecondaryFilterRegion;
    const hasValidSecondaryFilterValue = secondaryFilterOptionExists && activeSecondaryFilterRegion?.[0] !== ALL_OPTIONS.value;
    const shouldIncludeSecondaryRegionFilter = shouldShowSecondaryFilter && hasValidSecondaryFilterValue;
    const isTrendsAllChannels = showRevstackTrends && trendsSecondaryFilterChannel?.[0] === OptionValues.ALL;

    const cubeFilters = [
        ...(showRevstackTrends ? [
            {
                values: isTrendsAllChannels ? allChannelIds : trendsSecondaryFilterChannel?.length > 0 ? trendsSecondaryFilterChannel : revstackChannel,
                member: `${cubeId}.${RevstackFilterKey.CHANNEL}`,
                operator: "equals",
            }] : [{
                values: revstackChannel,
                member: `${cubeId}.${RevstackFilterKey.CHANNEL}`,
                operator: "equals",
            }]),
        {
            values: [clientSubGroupId],
            member: `${cubeId}.${RevstackFilterKey.CLIENT_SUBGROUP}`,
            operator: "equals",
        },
        {
            values: processRevstackFilterValues(
                categoryIds,
                filterKey === RevstackFilterKey.CATEGORY ? filterKey : null,
                filterKey === RevstackFilterKey.CATEGORY ? filterValue : null
            ),
            member: `${cubeId}.${RevstackFilterKey.CATEGORY}`,
            operator: "equals",
        },
        {
            values: processRevstackFilterValues(
                subcategoryIds,
                filterKey === RevstackFilterKey.SUBCATEGORY ? filterKey : null,
                filterKey === RevstackFilterKey.SUBCATEGORY ? filterValue : null
            ),
            member: `${cubeId}.${RevstackFilterKey.SUBCATEGORY}`,
            operator: "equals",
        },
        {
            values: brandIds,
            member: `${cubeId}.${brandFilterKey}`,
            operator: "equals",
        },
        ...(advancedFilters.length > 0 && cubeId !== RevstackCubeIds.KA_NATIONAL ? advancedFilters : []),
        ...(
            filterKey === RevstackFilterKey.REGION &&
                filterValue !== ALL_OPTIONS.value
                ? [
                    {
                        values: processRevstackFilterValues(
                            [],
                            filterKey,
                            filterValue
                        ),
                        member: `${cubeId}.${RevstackFilterKey.REGION}`,
                        operator: "equals",
                    },
                ]
                : shouldIncludeSecondaryRegionFilter && !isfiltersRequest
                    ? [{
                        values: processRevstackFilterValues(
                            [],
                            RevstackFilterKey.REGION,
                            activeSecondaryFilterRegion
                        ),
                        member: `${cubeId}.${RevstackFilterKey.REGION}`,
                        operator: "equals",
                    }]
                    : []
        ),
        // Drilldown Cross Channel SKU ID
        ...(showRevstackDrilldown && !showRevstackTrends && drilldownCrossChannelSkuId && drilldownCrossChannelSkuId[0] !== ALL_OPTIONS.value ?
            [{
                values: processRevstackFilterValues(
                    [],
                    RevstackFilterKey.CROSS_CHANNEL_SKU_ID,
                    drilldownCrossChannelSkuId
                ),
                member: `${cubeId}.${RevstackFilterKey.CROSS_CHANNEL_SKU_ID}`,
                operator: "equals",
            }]
            // Product Analysis Cross Channel SKU ID
            : shouldUsePACrossChannelSkuId && paCrossChannelSkuId ? [{
                values: processRevstackFilterValues(
                    [],
                    RevstackFilterKey.CROSS_CHANNEL_SKU_ID,
                    paCrossChannelSkuId
                ),
                member: `${cubeId}.${RevstackFilterKey.CROSS_CHANNEL_SKU_ID}`,
                operator: "equals",
            }] : []),
        ...(shouldUseSku ? [{
            values: processRevstackFilterValues(
                sku,
                filterKey === RevstackFilterKey.SKU ? filterKey : null,
                filterKey === RevstackFilterKey.SKU ? filterValue : null
            ),
            member: `${cubeId}.${skuFilterKey}`,
            operator: "equals",
        }] : []),
    ];

    return {
        cubeDateObject,
        cubeFilters,
    };
};

export const getRevstackMeasures = () =>
    filter(values(REVSTACK_MEASURES), (value: any) => value.isActive);

export const compare = (
    value: number,
    conditionValue: any,
    operator: string
) => {
    switch (operator) {
        case "greaterThan":
            return value > conditionValue;
        case "lessThan":
            return value < conditionValue;
        case "notEquals":
            return value !== conditionValue;
        case "equals":
            return value === conditionValue;
    }
};

export const applyFilterConfig = (
    data: any,
    config: any = {
        field: "overall_sov_value",
        condition: {
            operater: "greaterThan",
            value: 1,
        },
    }
) => {
    return data.filter((item: any) => {
        const value = item[config.field];

        // If the filter key doesn't exist in the data, exclude this item
        if (value === undefined || value === null) {
            return false;
        }

        const operator = config.condition.operater;
        const conditionValue = config.condition.value;
        return compare(value, conditionValue, operator);
    });
};

export const getPreparedMetric = (metric: CommonWidgetMetrics) => {
    let preparedMetric: any = metric;
    if (REVSTACK_MEASURES?.[metric]?.isMeasureDependent) {
        const filters = store.getState().revstackLayoutFilters.value;
        preparedMetric = `${metric}${reduce(
            REVSTACK_MEASURES?.[metric]?.dependencies,
            (acc: string, item: any) =>
                `${acc}${filters[item].selected ? `_${filters[item].selected}` : ""
                }`,
            ""
        )}`;
    }
    return preparedMetric;
};

export const formatMetric = (
    metric: CommonWidgetMetrics,
    value: any = "-",
    isChange = false,
    decimalPlaces?: number
) => {
    const currencySymbol = store.getState().channelCurrencySymbol.value;

    const isPriceMetric = metric === CommonWidgetMetrics.PRICE || metric === CommonWidgetMetrics.AVERAGE_PRICE;
    return value && !isNaN(value)
        ? `${isPriceMetric && !isChange ? currencySymbol : ""
        }${REVSTACK_MEASURES[metric]?.decimal === 0 || decimalPlaces === 0
            ? Math.round(Math.abs(value))
            : Math.abs(value).toFixed(
                REVSTACK_MEASURES[metric]?.decimal || 1
            )
        }${isChange
            ? REVSTACK_MEASURES[metric]?.changeUnit || ""
            : REVSTACK_MEASURES[metric]?.unit || ""
        }`
        : "-";
};

export const mergeUniqueByKey = <T, K extends keyof T>(
    array1: T[],
    array2: T[],
    key: K
): T[] => {
    return [
        ...array1,
        ...array2.filter(
            (item2) => !array1.some((item1) => item1[key] === item2[key])
        ),
    ];
};

export const sortNumericData = (
    allData: any[],
    sortKey: string,
    ascending: boolean
) => {
    allData = allData.sort((a: any, b: any) => {
        const aVal = Number(a[sortKey]) ?? -Infinity;
        const bVal = Number(b[sortKey]) ?? -Infinity;
        return ascending ? aVal - bVal : bVal - aVal;
    });
    return allData;
};
export const getMappedDataKey = (
    fil: any,
    widget: any,
    type: string
): string => {
    const dataKeyMap = widget?.config?.[type]?.dataKeyMap;
    if (!dataKeyMap) return fil.dataKey;

    const mappedFieldName = keys(dataKeyMap).find(
        (key) => dataKeyMap[key] === fil.dataKey
    );
    return mappedFieldName || fil.dataKey;
};

export const generateSelectOptions = (uniqueValues: Set<any>): any[] => {
    return Array.from(uniqueValues).map((value: any) => ({
        label: titleCase(value.toString()),
        value: value,
    }));
};

export const getUniqueValues = <
    T extends Record<string, any[]> | any[],
    K extends keyof T[keyof T] | string
>(
    data: T,
    key: K
): Set<any> => {
    const uniqueValues = new Set<any>();

    if (Array.isArray(data)) {
        // Handle array case directly
        data.forEach((item) => {
            const value = item[key as string];
            if (value !== null && value !== undefined) {
                uniqueValues.add(value);
            }
        });
    } else {
        // Handle object case (Record<string, any[]>)
        Object.keys(data).forEach((groupKey) => {
            const items = data[groupKey];
            if (Array.isArray(items)) {
                items.forEach((item) => {
                    const value = item[key as string];
                    if (value !== null && value !== undefined) {
                        uniqueValues.add(value);
                    }
                });
            }
        });
    }

    return uniqueValues;
};

export const removeDuplicatesByProperty = <T>(
    array: T[],
    propertyKey: keyof T
): T[] => {
    const seen = new Set();
    return array.filter((item) => {
        const value = item[propertyKey];
        if (seen.has(value)) {
            return false;
        }
        seen.add(value);
        return true;
    });
};

// Helper function to render pie chart outside labels
export const revstackRenderPieChartOutsideLabels = (
    chart: any,
    options: {
        labelDistance?: number;
        fontSize?: string;
        fontWeight?: string;
        color?: string;
        debug?: boolean;
    } = {}
) => {
    const {
        labelDistance = 10,
        fontSize = "12px",
        fontWeight = "bold",
        color = "#000000",
        debug = false,
    } = options;

    const series = chart.series[0];

    // Remove existing custom labels
    if (chart.customLabels) {
        chart.customLabels.forEach((label: any) => label.destroy());
    }
    chart.customLabels = [];

    series.points.forEach((point: any) => {
        if (point.graphic && point.visible !== false) {
            const centerX = chart.plotLeft + series.center[0];
            const centerY = chart.plotTop + series.center[1];
            const radius = series.center[2] / 2;
            const outerRadius = radius + labelDistance;

            // Calculate angle for label positioning using Highcharts angle method
            let angle;
            if (point.angle !== undefined) {
                angle = point.angle;
            } else if (
                point.startAngle !== undefined &&
                point.endAngle !== undefined
            ) {
                // Convert from degrees to radians if needed
                const startAngle = (point.startAngle * Math.PI) / 180;
                const endAngle = (point.endAngle * Math.PI) / 180;
                angle = (startAngle + endAngle) / 2;
            } else {
                // Fallback calculation
                angle = 0;
            }

            const labelX = centerX + Math.cos(angle) * outerRadius;
            const labelY = centerY + Math.sin(angle) * outerRadius;

            // Determine text alignment based on position
            let textAnchor = "middle";
            if (labelX > centerX + 15) textAnchor = "start";
            else if (labelX < centerX - 15) textAnchor = "end";

            // Create custom label with configurable styling
            const label = chart.renderer
                .text(
                    point.name,
                    labelX,
                    labelY + 5 // Slight vertical adjustment for better alignment
                )
                .attr({
                    "text-anchor": textAnchor,
                    zIndex: 1,
                    fill: color,
                })
                .css({
                    fontSize: fontSize,
                    fontWeight: fontWeight,
                    fontFamily: "Manrope, sans-serif",
                    color: color,
                    pointerEvents: "none",
                })
                .add();

            chart.customLabels.push(label);

            // Optional debug logging
            if (debug) {
                console.debug(`Created label for ${point.name}:`, {
                    startAngle: point.startAngle,
                    endAngle: point.endAngle,
                    angle: angle,
                    centerX: centerX,
                    centerY: centerY,
                    outerRadius: outerRadius,
                    labelX: labelX,
                    labelY: labelY,
                });
            }
        }
    });
};

export const isCategoricalMetric = (metric: string) => {
    const measureConfig = REVSTACK_MEASURES[metric];
    if (!measureConfig) return false;

    return measureConfig.isCategorical === true;
};

export const filterDrilldownTableData = (
    searchTerm: string,
    tableData: any[],
    activeTabColumns: any[]
) => {
    const pinnedColumns = activeTabColumns.filter(
        (col) => col.fixed === "left"
    );

    return tableData.filter((row: any, rowIndex: number) => {
        return pinnedColumns.some((col) => {
            const fieldValue = row[col.dataIndex];

            let renderedContent = fieldValue;
            if (col.render && typeof col.render === "function") {
                renderedContent = col.render(fieldValue, row, rowIndex);
            }

            let searchableText = "";
            if (typeof renderedContent === "string") {
                searchableText = renderedContent;
            } else if (renderedContent?.props?.children) {
                searchableText =
                    typeof renderedContent.props.children === "string"
                        ? renderedContent.props.children
                        : fieldValue?.toString() || "";
            } else {
                searchableText = fieldValue?.toString() || "";
            }

            return searchableText.toLowerCase().includes(searchTerm);
        });
    });
};

const generateAntTableCSVContent = (
    headers: string[],
    processedData: any[][]
) => {
    return [
        headers.join(","),
        ...processedData.map((row) =>
            row
                .map((cell) => {
                    const cellStr = String(cell || "");
                    if (
                        cellStr.includes(",") ||
                        cellStr.includes('"') ||
                        cellStr.includes("\n")
                    ) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                })
                .join(",")
        ),
    ].join("\n");
};

const downloadCSV = (csvContent: string, fileName: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, fileName);
};

export const exportAntTableData = (
    tableData: any[],
    columns: any[],
    format: TableExportFormats = TableExportFormats.CSV
) => {
    if (!tableData || tableData.length === 0) {
        showToast("error", "No data to export");
        return;
    }

    const exportableColumns = columns.filter((col) => {
        return (
            (col.dataIndex && col.title && !col.key?.includes("action")) ||
            (col.children && col.children.length > 0)
        );
    });

    const flattenedColumns: any[] = [];
    exportableColumns.forEach((col) => {
        if (col.children && col.children.length > 0) {
            flattenedColumns.push(...col.children);
        } else {
            flattenedColumns.push(col);
        }
    });

    const headers = flattenedColumns.map((col) => {
        // Handle JSX/React elements in title
        if (React.isValidElement(col.title)) {
            // Extract text from JSX element
            const extractTextFromJSX = (element: React.ReactElement): string => {
                if (typeof element.props.children === 'string') {
                    return element.props.children;
                }
                if (Array.isArray(element.props.children)) {
                    return element.props.children
                        .map((child: any) => {
                            if (typeof child === 'string') return child;
                            if (React.isValidElement(child)) return extractTextFromJSX(child);
                            return '';
                        })
                        .join(' ')
                        .trim();
                }
                // Handle component names safely
                const componentName = typeof element.type === 'string'
                    ? element.type
                    : (element.type as any)?.name || 'Column';
                return componentName;
            };
            return extractTextFromJSX(col.title);
        }
        // Handle string titles
        if (typeof col.title === 'string') {
            return col.title;
        }
        // Handle other cases (numbers, etc.)
        return String(col.title || 'Column');
    });

    // Helper function to access nested object properties using array of keys
    const getNestedValue = (obj: any, keyPath: any[]): any => {
        return keyPath.reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : undefined;
        }, obj);
    };

    const processedData = tableData.map((row) => {
        return flattenedColumns.map((col) => {
            // Handle array-based dataIndex for nested object access
            let fieldValue;
            if (Array.isArray(col.dataIndex)) {
                fieldValue = getNestedValue(row, col.dataIndex);
            } else {
                fieldValue = row[col.dataIndex];
            }

            let exportValue = fieldValue;

            if (col.render && typeof col.render === "function") {
                if (
                    fieldValue !== null &&
                    fieldValue !== undefined &&
                    fieldValue !== ""
                ) {
                    exportValue = fieldValue;
                } else {
                    // For array-based dataIndex, also try accessing via key path
                    if (Array.isArray(col.dataIndex) && col.key) {
                        const keyPath = [...col.dataIndex];
                        // Replace the last element with the key for fallback access
                        keyPath[keyPath.length - 1] = col.key;
                        const fallbackValue = getNestedValue(row, keyPath);
                        if (
                            fallbackValue !== undefined &&
                            fallbackValue !== null &&
                            fallbackValue !== ""
                        ) {
                            exportValue = fallbackValue;
                        }
                    } else if (
                        col.key &&
                        row[col.key] !== undefined &&
                        row[col.key] !== null &&
                        row[col.key] !== ""
                    ) {
                        exportValue = row[col.key];
                    }
                }
            }

            if (exportValue === null || exportValue === undefined) {
                return "";
            }

            const stringValue = String(exportValue).trim();
            const cleanValue = stringValue.replace(/<[^>]*>/g, "");

            return cleanValue;
        });
    });

    const clientName = (() => {
        try {
            const clientElement = document.getElementsByClassName(
                "client-info"
            )[0] as HTMLElement;
            return (
                clientElement?.innerText
                    ?.split(" ")
                    ?.join("-")
                    ?.replace(/[().]/g, "") || "Client"
            );
        } catch {
            return "Client";
        }
    })();

    const moduleName = getActiveModule().activeModule;
    const timestamp = moment().format("DD-MM-YYYY-HH-mm");
    const randomString = generateRandomString(6);
    const fileExtension = format === TableExportFormats.EXCEL ? "xlsx" : "csv";
    const fileName = `Reviniti-${clientName}-${moduleName}-${timestamp}-${randomString}.${fileExtension}`;

    if (format === TableExportFormats.CSV) {
        const csvContent = generateAntTableCSVContent(headers, processedData);
        downloadCSV(csvContent, fileName);
    } else if (format === TableExportFormats.EXCEL) {
        try {
            // @ts-ignore - Dynamic import for optional dependency
            import("xlsx")
                .then((XLSX: any) => {
                    const worksheetData = [headers, ...processedData];
                    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, moduleName);
                    const excelBuffer = XLSX.write(workbook, {
                        bookType: "xlsx",
                        type: "array",
                    });

                    const blob = new Blob([excelBuffer], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    saveAs(blob, fileName);
                })
                .catch((error) => {
                    console.error("Error loading xlsx library:", error);
                    console.warn(
                        "Excel export requires xlsx library. Falling back to CSV export."
                    );
                    const csvContent = generateAntTableCSVContent(
                        headers,
                        processedData
                    );
                    const fallbackFileName = fileName.replace(".xlsx", ".csv");
                    downloadCSV(csvContent, fallbackFileName);
                    showToast(
                        "error",
                        "Error creating Excel file, downloading CSV instead"
                    );
                });
        } catch (error) {
            console.error("Error creating Excel file:", error);
            showToast("error", "Error creating Excel file");
            const csvContent = generateAntTableCSVContent(
                headers,
                processedData
            );
            const fallbackFileName = fileName.replace(".xlsx", ".csv");
            downloadCSV(csvContent, fallbackFileName);
        }
    }

    showToast("success", `${fileName} exported successfully`);

    return fileName;
};

export const competitionButtonHandler = <T extends ICompetitionDataSourceItem>(
    dataSource: T[], 
    setHideCompetition: TSetHideCompetition
): T[] => {
    let hideCompetitionBrands: boolean;
    setHideCompetition((prev: boolean) => {
        hideCompetitionBrands = !prev;
        return hideCompetitionBrands;
    });
    const selfBrandIds = store.getState().mainFiltersData?.value?.brands?.filter((item: IRevstackFilterOption) => item.brandType === BrandTypes.MY_BRAND)?.map((item: IRevstackFilterOption) => +item.value);
    const selfBrands = dataSource.filter((item: T) => selfBrandIds?.includes(+item.brand_id));
    if (hideCompetitionBrands!) {
        return selfBrands;
    } else {
        return dataSource;
    }
}

export const createColumnVisibilityMenu = (colDefs: any[], colVisibilityMode: "group" | "columns" = "columns") => {
    const columnsToProcess: any[] = [];

    if (colVisibilityMode === "group") {
        // Extract groups (parent columns with children)
        colDefs?.forEach((col: any) => {
            if (col.children && col.children.length > 0) {
                // This is a group header
                columnsToProcess.push(col);
            }
        });
    } else {
        // Default to "columns" mode - extract individual columns from nested structure
        const extractColumns = (cols: any[]) => {
            cols?.forEach((col: any) => {
                if (col.children && col.children.length > 0) {
                    // This is a group with children, extract the children
                    extractColumns(col.children);
                } else if (col.key) {
                    // This is an actual data column
                    columnsToProcess.push(col);
                }
            });
        };

        extractColumns(colDefs);
    }

    // Then filter the processed columns/groups
    return columnsToProcess?.filter((col: any) => {
        if (colVisibilityMode === "group") {
            // For group mode, filter less strictly - mainly check if it has a title
            if (!col.title || col.title === "") {
                return false;
            }
            // Groups typically don't have keys, so we don't filter by key
            return true;
        } else {
            // For individual column mode, apply strict filtering
            // [1] Filter out columns with empty or missing titles/keys
            if (!col.key || col.title === undefined || col.title === "") {
                return false;
            }

            // [2] Filter out pinned columns (usually the first column and essential columns)
            if (col.fixed === "left" || col.fixed === true) {
                return false;
            }

            // [3] Filter out drilldown/action columns (interactive functionality columns)
            if (col.key && (
                col.key.toString().includes("drilldown") ||
                col.key.toString().includes("action") ||
                col.dataIndex && col.dataIndex.toString().includes("drilldown")
            )) {
                return false;
            }

            return true;
        }
    }).map((col: any) => {
        let label = col.title;

        // [3] If title is a React element, try to extract text content
        if (typeof col.title === 'object' && col.title !== null) {
            if (col.title.props && col.title.props.children) {
                label = typeof col.title.props.children === 'string'
                    ? col.title.props.children
                    : col.key;
            } else {
                label = col.key;
            }
        }

        if (typeof label !== 'string') {
            label = col.key;
        }

        if (!label || label === col.key) {
            label = col.key.toString().replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
            label = label.charAt(0).toUpperCase() + label.slice(1);
        }

        return {
            key: col.key || col.title, // For groups, use title as key if no key exists
            value: col.key || col.title,
            label: label,
        };
    }) || [];
}

// Helper function to create gradient fill color for area charts
export const createLineChartGradientFillColor = (color: string) => ({
    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
    stops: [
        [0, Highcharts.color(color).setOpacity(0.05).get("rgba") as string],
        [0.5, Highcharts.color(color).setOpacity(0.03).get("rgba") as string],
        [1, Highcharts.color(color).setOpacity(0.01).get("rgba") as string],
    ],
});

export const dispatchUpdateTrendsState = (
    dispatch: any,
    widgetConfig: any,
    titlePills: string[] = []
) => {
    dispatch(updateTrendsState({
        show: true,
        activeSectionId: widgetConfig.sectionId,
        module: widgetConfig?.config?.module,
        defaultTab: widgetConfig?.config?.defaultTab,
        hideTrendsTabSection: widgetConfig?.config?.hideTrendsTabSection,
        titlePills: titlePills,
    }))
};


// Common helper function for sorting based on filters data
export const sortChartDataByFiltersDataOrder = (
    items: any[],
    dropDownFiltersData: any[],
    itemKey: string,
    sortByFiltersData = true
): any[] => {
    if (!sortByFiltersData || !dropDownFiltersData || dropDownFiltersData.length === 0) {
        return items;
    }

    // Create a map of filters data order for quick lookup
    const filtersOrderMap = new Map();
    dropDownFiltersData.forEach((filterItem: any, index: number) => {
        const filterValue = filterItem[itemKey];
        if (filterValue) {
            filtersOrderMap.set(filterValue, index);
        }
    });

    // Sort items based on the order in filters data
    return items.sort((a: any, b: any) => {
        const aValue = a[itemKey] || a;
        const bValue = b[itemKey] || b;

        const aOrder = filtersOrderMap.has(aValue) ? filtersOrderMap.get(aValue) : Number.MAX_SAFE_INTEGER;
        const bOrder = filtersOrderMap.has(bValue) ? filtersOrderMap.get(bValue) : Number.MAX_SAFE_INTEGER;

        return aOrder - bOrder;
    });
};

export const getRevstackMainFilterOptions = (params: {
    type: string;
    allOptions?: { label: string; value: string | number };
}) => {
    const { type, allOptions } = params;
    const mainFiltersData = store.getState().mainFiltersData.value?.[type];
    const selectedFilters = store.getState().mainFilters.value[type];

    if (!mainFiltersData || !selectedFilters) {
        return allOptions ? [allOptions] : [];
    }

    const selectedFiltersSet = new Set(selectedFilters);
    const options = mainFiltersData.filter((opt: any) => selectedFiltersSet.has(opt.value))?.map((opt: any) => ({
        label: capitalizeWords(opt.label),
        value: opt.value,
    }));
    return allOptions ? [allOptions, ...options] : options;
}

export const mergePercentAndNonPercentData = (data: any[], recordKey?: string) => {

    if (!Array.isArray(data) || !recordKey) return [];

    const metricGroups: any[][] = [];

    data.forEach(obj => {
        Object.keys(obj).forEach(key => {
            const lower = key.toLowerCase();
            if (lower.includes("percent")) {
                metricGroups.push(obj[key] || []);
            }
        });
    });

    if (metricGroups.length === 0) return [];

    const mergedMap = new Map<string, any>();

    metricGroups.forEach(group => {
        group.forEach(item => {
            const identifier = item[recordKey];
            if (!identifier) return;

            const key = String(identifier);
            if (!mergedMap.has(key)) mergedMap.set(key, {});
            Object.assign(mergedMap.get(key), item);
        });
    });

    return Array.from(mergedMap.values());
};

export const setAdvancedFilters = async (
    categories: number[],
    subcategories: number[],
    brands: number[]
): Promise<void> => {
    try {
        const categoryIds = filter(categories || [], (category: number) => category > 0);
        const subcategoryIds = filter(subcategories || [], (subcategory: number) => subcategory > 0);
        const brandIds = filter(brands || [], (brand: number) => brand > 0);

        const clientTagTypes = await getAdvancedFilters({
            categoryIds,
            subcategoryIds,
            brandIds
        });

        const recordTypeTags = map(
            clientTagTypes,
            (type) => {
                
                const tagsArray = (type.tags && Array.isArray(type.tags))
                    ? (type.tags as unknown as Array<{ tag: string; tag_id: number }>)
                    : [];

                const options: Array<{ label: string; value: number }> = tagsArray.map((tagObj) => ({
                    label: tagObj.tag,
                    value: tagObj.tag_id,
                }));

                return {
                    label: type.tag_type,
                    value: type.tag_type_id,
                    options,
                };
            }
        );

        store.dispatch(updateAdditionalFiltersData({ tagTypes: recordTypeTags }));
    } catch (err:any) {
        console.error(err.message);
        Sentry.captureException(err.message);
        store.dispatch(updateAdditionalFiltersData({ tagTypes: [] }));
    }
};